import {
  type FC,
  type HTMLAttributes,
  isValidElement,
  type ReactNode,
} from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { mergeClasses } from "@huub-web/utils";

import {
  containerVariantStyle,
  borderedContainerStyle,
  containerBaseStyle,
  badgeStyle,
  iconStyle,
} from "./Indicator.css";
import { Icon, type IconProps } from "../Icon/Icon";
import { Text } from "../Text/Text";
import { Badge } from "../Badge/Badge";
import type { IconNodeProp } from "../../props";

export type IndicatorProps = HTMLAttributes<HTMLDivElement> &
  RecipeVariants<typeof containerBaseStyle> &
  RecipeVariants<typeof containerVariantStyle> &
  IconNodeProp & {
    bordered?: boolean;
    completed?: boolean;
    iconSize?: IconProps["size"];
    total?: number;
    trailing?: ReactNode;
  };

/**
 * Indicator component.
 */
export const Indicator: FC<IndicatorProps> = ({
  icon = "add",
  variant,
  bordered = false,
  completed = false,
  inline = false,
  type,
  size,
  iconSize = "m",
  total,
  className,
  trailing,
  children,
  ...props
}) => (
  <div
    className={mergeClasses(
      containerBaseStyle({ size, type, inline }),
      bordered
        ? borderedContainerStyle({ variant })
        : containerVariantStyle({ variant }),
      className,
    )}
    {...props}
  >
    {children !== undefined ? (
      <Text
        variant={
          size === "xxs"
            ? "bodyS"
            : size === "xs"
              ? "bodyS"
              : size === "m"
                ? "bodyL"
                : "bodyM"
        }
      >
        {children}
      </Text>
    ) : isValidElement(icon) ? (
      icon
    ) : (
      <Icon className={iconStyle({ size })} icon={icon} size={iconSize} />
    )}

    {completed && (
      <Badge
        variant="success"
        value={<Icon icon="checkThick" size="xs" />}
        className={badgeStyle}
        offset={size === "m" ? "small" : "default"}
      />
    )}

    {total !== undefined && total > 0 && !completed && (
      <Badge variant="primary" value={total} max={99} className={badgeStyle} />
    )}

    {trailing}
  </div>
);
